import * as React from 'react';
import { Box } from '@material-ui/core';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { FormikTextField } from '@components/fieldBindings/FormikTextField';
import { FormikSubmitButton } from '@components/fieldBindings/FormikSubmitButton';
import client from '@api/client';
import i18n from '@src/i18n';
import { CenterColumnPage } from '../../Layouts/CenterColumnPage/CenterColumnPage';

type AdminCreateUserFormValues = {
  email: string;
  adminPassword: string;
};

const validationSchema = Yup.object().shape({
  email: Yup.string().trim().email(i18n.t('modals.signUp.email.invalid')).required(i18n.t('common.required')),
  adminPassword: Yup.string().required(i18n.t('common.required')),
});

export const AdminCreateUser: React.FC = () => {
  const { t } = useTranslation();

  const handleSubmit = async (
    { email, adminPassword }: AdminCreateUserFormValues,
    util: FormikHelpers<AdminCreateUserFormValues>
  ) => {
    try {
      await client.auth.adminCreateUser(email, adminPassword);
      toast.success(t('pages.adminCreateUser.successMsg') as string);
      util.resetForm({ values: { email: '', adminPassword } });
    } catch (err) {
      toast.error(t('pages.adminCreateUser.errorMsg') as string);
      util.setSubmitting(false);
    }
  };

  return (
    <CenterColumnPage pageTitle={t('pages.adminCreateUser.titleText') as string}>
      <Formik<AdminCreateUserFormValues>
        onSubmit={handleSubmit}
        initialValues={{ email: '', adminPassword: '' }}
        validateOnBlur={false}
        validationSchema={validationSchema}
      >
        {({ isValid, dirty }) => (
          <Form>
            <Box display="flex" flexDirection="column" mb={2}>
              <FormikTextField
                id="email"
                name="email"
                type="email"
                placeholder={t('pages.adminCreateUser.email.placeholder')}
                margin="normal"
                autoComplete="email"
                autoFocus
              />
              <FormikTextField
                id="adminPassword"
                name="adminPassword"
                type="password"
                placeholder={t('pages.adminCreateUser.adminPassword.placeholder')}
                margin="normal"
                autoComplete="off"
              />
            </Box>
            <FormikSubmitButton disabled={!(isValid && dirty)}>
              {t('pages.adminCreateUser.submitButtonText')}
            </FormikSubmitButton>
          </Form>
        )}
      </Formik>
    </CenterColumnPage>
  );
};

export default AdminCreateUser;
